import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { ProvidersFilterOptionsType } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { ProviderMapResults } from "@app/projects/results/common/mapping";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ZipProviderFilters } from "@app/projects/zip-projects/maps/interfaces";
import { DEFAULT_CONTRIBUTION_RANGE } from "@app/projects/zip-projects/maps/ZipCoverageMapConstants";

export type ZipProviderFilterChildOption = NonNullable<ProvidersFilterOptionsType["children"]>[number];

export const DEFAULT_ZIP_PROVIDER_FILTERS: ZipProviderFilters = {
  gainsRange: DEFAULT_CONTRIBUTION_RANGE,
  impactRange: DEFAULT_CONTRIBUTION_RANGE,
  providerFilterOptions: [],
  servesRange: DEFAULT_CONTRIBUTION_RANGE,
  showExcludedProviders: false,
};

const isTargetedOrExcluded = (provider: ProviderMapResults): boolean =>
  provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted ||
  provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded;

const isSource = (provider: ProviderMapResults, source: ProviderNetworkTypeEnum): boolean =>
  provider.providerNetworkType === source;

const SOURCE_STATUS_RULES: Partial<Record<ProvidersFilterOptions, (provider: ProviderMapResults) => boolean>> = {
  [ProvidersFilterOptions.AvailableMarket]: (p) => isSource(p, ProviderNetworkTypeEnum.Market) && !isTargetedOrExcluded(p),
  [ProvidersFilterOptions.AvailableProspect]: (p) => isSource(p, ProviderNetworkTypeEnum.Prospect) && !isTargetedOrExcluded(p),
  [ProvidersFilterOptions.ExcludedMarket]: (p) => isSource(p, ProviderNetworkTypeEnum.Market) && p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded,
  [ProvidersFilterOptions.ExcludedProspect]: (p) => isSource(p, ProviderNetworkTypeEnum.Prospect) && p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded,
  [ProvidersFilterOptions.NonTerminated]: (p) => isSource(p, ProviderNetworkTypeEnum.Network) && p.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTerminated,
  [ProvidersFilterOptions.TargetedMarket]: (p) => isSource(p, ProviderNetworkTypeEnum.Market) && p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted,
  [ProvidersFilterOptions.TargetedProspect]: (p) => isSource(p, ProviderNetworkTypeEnum.Prospect) && p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted,
  [ProvidersFilterOptions.Terminated]: (p) => isSource(p, ProviderNetworkTypeEnum.Network) && p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated,
};

const matchesSourceAndActionStatus = (
  provider: ProviderMapResults,
  selectedOptions: ProvidersFilterOptions[],
): boolean => {
  if (selectedOptions.length === 0) {
    return true;
  }

  return selectedOptions.some((option) => SOURCE_STATUS_RULES[option]?.(provider) === true);
};

const matchesContributionMeasures = (provider: ProviderMapResults, filters: ZipProviderFilters): boolean => {
  const [servesMin, servesMax] = filters.servesRange;
  if (provider.servesPercent < servesMin || provider.servesPercent > servesMax) {
    return false;
  }

  if (provider.providerNetworkType === ProviderNetworkTypeEnum.Network) {
    return true;
  }

  const [gainsMin, gainsMax] = filters.gainsRange;
  if (provider.gainsPercent < gainsMin || provider.gainsPercent > gainsMax) {
    return false;
  }

  const [impactMin, impactMax] = filters.impactRange;
  if (provider.impactsPercent < impactMin || provider.impactsPercent > impactMax) {
    return false;
  }

  return true;
};

export const filterZipProviders = (
  providers: ProviderMapResults[],
  filters: ZipProviderFilters,
): ProviderMapResults[] =>
  (providers ?? []).filter((provider) =>
    matchesSourceAndActionStatus(provider, filters.providerFilterOptions) &&
    (filters.showExcludedProviders || provider.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyExcluded) &&
    matchesContributionMeasures(provider, filters));

const nonTerminatedFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-non-terminated",
  name: "Non-Terminated",
  type: ProvidersFilterOptions.NonTerminated,
};

const terminatedFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-terminated",
  name: "Terminated",
  type: ProvidersFilterOptions.Terminated,
};

const availableMarketFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-market-available",
  name: "Available",
  type: ProvidersFilterOptions.AvailableMarket,
};

const targetedMarketFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-market-targeted",
  name: "Targeted",
  type: ProvidersFilterOptions.TargetedMarket,
};

const excludedMarketFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-excluded-market",
  name: "Excluded",
  type: ProvidersFilterOptions.ExcludedMarket,
};

const availableProspectFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-prospect-available",
  name: "Available",
  type: ProvidersFilterOptions.AvailableProspect,
};

const targetedProspectFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-prospect-targeted",
  name: "Targeted",
  type: ProvidersFilterOptions.TargetedProspect,
};

const excludedProspectFilterOption: ZipProviderFilterChildOption = {
  id: "provider-accordion-filter-excluded-prospect",
  name: "Excluded",
  type: ProvidersFilterOptions.ExcludedProspect,
};

const baseProviderFilterOptionsTemplate: ProvidersFilterOptionsType[] = [
  {
    children: [],
    id: "provider-accordion-filter-network",
    name: "Network Providers",
    type: ProvidersFilterOptions.Network,
  },
  {
    children: [],
    id: "provider-accordion-filter-market",
    name: "Market Providers",
    type: ProvidersFilterOptions.Market,
  },
  {
    children: [],
    id: "provider-accordion-filter-prospect",
    name: "Prospect Providers",
    type: ProvidersFilterOptions.Prospect,
  },
];

export const buildZipProviderFilterOptionTree = (
  providers: ProviderMapResults[],
  options: { canViewTerminations: boolean; isLatestPublished: boolean },
): ProvidersFilterOptionsType[] => {
  const all = providers ?? [];
  const networkProviders = all.filter((p) => p.providerNetworkType === ProviderNetworkTypeEnum.Network);
  const impactProviders = all.filter((p) => p.providerNetworkType !== ProviderNetworkTypeEnum.Network);

  const hasAvailable = (source: ProviderNetworkTypeEnum): boolean =>
    impactProviders.some((p) => p.providerNetworkType === source && !isTargetedOrExcluded(p));

  const hasStatus = (source: ProviderNetworkTypeEnum, status: ProviderActionStatusEnum): boolean =>
    impactProviders.some((p) => p.providerNetworkType === source && p.scenarioModelProviderStatus === status);

  const hasNonTerminated =
    networkProviders.some((p) => p.scenarioModelProviderStatus !== ProviderActionStatusEnum.FullyTerminated) ||
    (networkProviders.length > 0 && !options.canViewTerminations);

  const hasTerminated =
    options.isLatestPublished &&
    options.canViewTerminations &&
    networkProviders.some((p) => p.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated);

  const childrenFor = (parent: ProvidersFilterOptions): ZipProviderFilterChildOption[] => {
    switch (parent) {
      case ProvidersFilterOptions.Network:
        return [
          ...(hasNonTerminated ? [nonTerminatedFilterOption] : []),
          ...(hasTerminated ? [terminatedFilterOption] : []),
        ];
      case ProvidersFilterOptions.Market:
        return [
          ...(hasAvailable(ProviderNetworkTypeEnum.Market) ? [availableMarketFilterOption] : []),
          ...(options.isLatestPublished && hasStatus(ProviderNetworkTypeEnum.Market, ProviderActionStatusEnum.FullyTargeted) ? [targetedMarketFilterOption] : []),
          ...(options.isLatestPublished && hasStatus(ProviderNetworkTypeEnum.Market, ProviderActionStatusEnum.FullyExcluded) ? [excludedMarketFilterOption] : []),
        ];
      case ProvidersFilterOptions.Prospect:
        return [
          ...(hasAvailable(ProviderNetworkTypeEnum.Prospect) ? [availableProspectFilterOption] : []),
          ...(options.isLatestPublished && hasStatus(ProviderNetworkTypeEnum.Prospect, ProviderActionStatusEnum.FullyTargeted) ? [targetedProspectFilterOption] : []),
          ...(options.isLatestPublished && hasStatus(ProviderNetworkTypeEnum.Prospect, ProviderActionStatusEnum.FullyExcluded) ? [excludedProspectFilterOption] : []),
        ];
      default:
        return [];
    }
  };

  return baseProviderFilterOptionsTemplate.map((option) => ({ ...option, children: childrenFor(option.type) }));
};

export const eligibleFilterChildren = (
  option: ProvidersFilterOptionsType,
  showExcludedProviders: boolean,
): ZipProviderFilterChildOption[] => {
  const children = option.children ?? [];
  if (showExcludedProviders) {
    return children;
  }

  return children.filter(
    (child) =>
      child.type !== ProvidersFilterOptions.ExcludedMarket &&
      child.type !== ProvidersFilterOptions.ExcludedProspect,
  );
};

const preferredProviderSourceFilterOrder: ProvidersFilterOptions[] = [
  ProvidersFilterOptions.Network,
  ProvidersFilterOptions.NonTerminated,
  ProvidersFilterOptions.Terminated,
  ProvidersFilterOptions.Market,
  ProvidersFilterOptions.AvailableMarket,
  ProvidersFilterOptions.TargetedMarket,
  ProvidersFilterOptions.ExcludedMarket,
  ProvidersFilterOptions.Prospect,
  ProvidersFilterOptions.AvailableProspect,
  ProvidersFilterOptions.TargetedProspect,
  ProvidersFilterOptions.ExcludedProspect,
];

export const providerSourceFiltersComparer = (a: ProvidersFilterOptions, b: ProvidersFilterOptions): number => {
  const firstIndex = preferredProviderSourceFilterOrder.indexOf(a);
  const secondIndex = preferredProviderSourceFilterOrder.indexOf(b);

  if (firstIndex === -1 || secondIndex === -1) {
    if (firstIndex === -1 && secondIndex !== -1) return 1;
    if (firstIndex !== -1 && secondIndex === -1) return -1;
    return 0;
  }

  return firstIndex - secondIndex;
};
